/**
 * Leviathan Web - Bill Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface BillItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  lineTotal: number;
  product?: {
    id: string;
    name: string;
    sku: string;
  };
}

interface BillPayment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  paymentMethod: string;
  checkNumber: string | null;
  referenceNumber: string | null;
  notes: string | null;
  createdAt: string;
}

interface Bill {
  id: string;
  billNumber: string;
  billDate: string;
  dueDate: string;
  paidDate: string | null;
  status: string;
  paymentTerms: string | null;
  vendorInvoiceNumber: string | null;
  vendorInvoiceDate: string | null;
  subtotal: number;
  taxAmount: number;
  shippingAmount: number;
  otherCharges: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
    email: string | null;
    phone: string | null;
  };
  receipt?: {
    id: string;
    receiptNumber: string;
  };
  items: BillItem[];
  payments: BillPayment[];
}

const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['PENDING', 'VOID'],
  PENDING: ['APPROVED', 'VOID'],
  APPROVED: ['SCHEDULED', 'PARTIAL', 'PAID', 'VOID'],
  SCHEDULED: ['PARTIAL', 'PAID', 'APPROVED', 'VOID'],
  PARTIAL: ['PAID', 'VOID'],
  PAID: [],
  VOID: [],
};

const BillDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [bill, setBill] = useState<Bill | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'CHECK',
    paymentDate: new Date().toISOString().split('T')[0] || '',
    checkNumber: '',
    referenceNumber: '',
    notes: '',
  });
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  useEffect(() => {
    fetchBill();
  }, [id]);

  const fetchBill = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bills/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBill(data);
        // Set default payment amount to amount due
        setPaymentData((prev) => ({
          ...prev,
          amount: data.amountDue.toString(),
        }));
      } else {
        setError('Bill not found');
      }
    } catch (error) {
      console.error('Failed to fetch bill:', error);
      setError('Failed to load bill');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus: string) => {
    if (!bill) return;
    setIsUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/bills/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        setBill(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const recordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bill) return;
    setIsRecordingPayment(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/bills/${id}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          amount: paymentData.amount,
          paymentMethod: paymentData.paymentMethod,
          paymentDate: paymentData.paymentDate || null,
          checkNumber: paymentData.checkNumber || null,
          referenceNumber: paymentData.referenceNumber || null,
          notes: paymentData.notes || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setBill(data);
        setShowPaymentModal(false);
        setPaymentData({
          amount: data.amountDue.toString(),
          paymentMethod: 'CHECK',
          paymentDate: new Date().toISOString().split('T')[0] || '',
          checkNumber: '',
          referenceNumber: '',
          notes: '',
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      setError('Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const deleteBill = async () => {
    if (!bill || bill.status !== 'DRAFT') return;
    if (!confirm('Are you sure you want to delete this bill?')) return;

    try {
      const response = await fetch(`${API_URL}/api/bills/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        navigate('/ap');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete bill');
      }
    } catch (error) {
      console.error('Failed to delete bill:', error);
      setError('Failed to delete bill');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (['PAID', 'VOID', 'DRAFT', 'PENDING'].includes(status)) return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      case 'PENDING':
        return 'text-steel bg-steel/20 border-steel/30';
      case 'APPROVED':
        return 'text-amber bg-amber/20 border-amber/30';
      case 'SCHEDULED':
        return 'text-amber bg-amber/20 border-amber/30';
      case 'PARTIAL':
        return 'text-warning-amber bg-warning-amber/20 border-warning-amber/30';
      case 'PAID':
        return 'text-success-green bg-success-green/20 border-success-green/30';
      case 'VOID':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      default:
        return 'text-text-secondary bg-text-secondary/20 border-text-secondary/30';
    }
  };

  const getStatusButtonColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-steel/20 hover:bg-steel/30 text-steel border-steel/30';
      case 'APPROVED':
        return 'bg-amber/20 hover:bg-amber/30 text-amber border-amber/30';
      case 'SCHEDULED':
        return 'bg-amber/20 hover:bg-amber/30 text-amber border-amber/30';
      case 'PARTIAL':
        return 'bg-warning-amber/20 hover:bg-warning-amber/30 text-warning-amber border-warning-amber/30';
      case 'PAID':
        return 'bg-success-green/20 hover:bg-success-green/30 text-success-green border-success-green/30';
      case 'VOID':
        return 'bg-text-tertiary/20 hover:bg-text-tertiary/30 text-text-tertiary border-text-tertiary/30';
      default:
        return 'bg-panel-border hover:bg-steel/20 text-text-primary border-panel-border';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading bill...</div>
      </div>
    );
  }

  if (error && !bill) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="text-error-red font-mono mb-4">&gt; {error}</div>
        <Link to="/ap" className="text-steel hover:text-amber font-mono transition-colors">
          &larr; Back to Accounts Payable
        </Link>
      </div>
    );
  }

  if (!bill) return null;

  const validTransitions = VALID_STATUS_TRANSITIONS[bill.status] || [];
  const canRecordPayment = ['APPROVED', 'SCHEDULED', 'PARTIAL'].includes(bill.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  {bill.billNumber}
                </h1>
                <span
                  className={`px-3 py-1 font-mono text-sm uppercase tracking-wider border ${getStatusColor(bill.status)}`}
                >
                  {bill.status}
                </span>
                {isOverdue(bill.dueDate, bill.status) && (
                  <span className="px-3 py-1 font-mono text-sm uppercase tracking-wider border text-error-red bg-error-red/20 border-error-red/30 animate-pulse">
                    OVERDUE
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/ap"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Accounts Payable
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {canRecordPayment && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="px-6 py-2 bg-success-green/20 hover:bg-success-green/30 text-success-green font-mono text-sm uppercase tracking-wider transition-all border border-success-green/30"
                >
                  Record Payment
                </button>
              )}
              {bill.status === 'DRAFT' && (
                <button
                  onClick={deleteBill}
                  className="px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono text-sm uppercase tracking-wider transition-all border border-error-red/30"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Status Actions */}
      {validTransitions.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Status Actions
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {validTransitions.map((status) => (
                <button
                  key={status}
                  onClick={() => updateStatus(status)}
                  disabled={isUpdating}
                  className={`px-6 py-2 font-mono text-sm uppercase tracking-wider transition-all border disabled:opacity-50 ${getStatusButtonColor(status)}`}
                >
                  {isUpdating ? 'Updating...' : `Mark as ${status}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bill Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={formatCurrency(bill.total)} color="amber" />
        <StatCard label="Amount Paid" value={formatCurrency(bill.amountPaid)} color="green" />
        <StatCard
          label="Amount Due"
          value={formatCurrency(bill.amountDue)}
          color={bill.amountDue > 0 ? 'red' : 'green'}
        />
        <StatCard label="Due Date" value={formatDate(bill.dueDate)} color="steel" />
      </div>

      {/* Bill Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supplier Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Supplier
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <Link
                  to={`/suppliers/${bill.supplier.id}`}
                  className="text-text-primary font-mono font-semibold text-lg hover:text-amber transition-colors"
                >
                  {bill.supplier.name}
                </Link>
                <div className="text-text-tertiary font-mono text-sm">
                  {bill.supplier.supplierNumber}
                </div>
              </div>
              {bill.supplier.email && (
                <div className="text-text-secondary font-mono text-sm">
                  Email: {bill.supplier.email}
                </div>
              )}
              {bill.supplier.phone && (
                <div className="text-text-secondary font-mono text-sm">
                  Phone: {bill.supplier.phone}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bill Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Bill Details
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Bill Date</div>
                <div className="text-text-primary font-mono">{formatDate(bill.billDate)}</div>
              </div>
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Due Date</div>
                <div
                  className={`font-mono ${isOverdue(bill.dueDate, bill.status) ? 'text-error-red' : 'text-text-primary'}`}
                >
                  {formatDate(bill.dueDate)}
                </div>
              </div>
              {bill.paymentTerms && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Payment Terms</div>
                  <div className="text-text-primary font-mono">{bill.paymentTerms}</div>
                </div>
              )}
              {bill.vendorInvoiceNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Vendor Invoice #</div>
                  <div className="text-text-primary font-mono">{bill.vendorInvoiceNumber}</div>
                </div>
              )}
              {bill.receipt && (
                <div className="col-span-2">
                  <div className="text-text-tertiary font-mono uppercase text-xs">From Receipt</div>
                  <Link
                    to={`/receipts/${bill.receipt.id}`}
                    className="text-steel hover:text-amber font-mono transition-colors"
                  >
                    {bill.receipt.receiptNumber}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          <div className="p-6 border-b border-panel-border">
            <div className="flex items-center space-x-2">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Line Items
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Description
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Quantity
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Unit Cost
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Line Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {bill.items.map((item) => (
                  <tr key={item.id} className="border-b border-panel-border">
                    <td className="px-6 py-4">
                      <div className="text-text-primary font-mono font-semibold">
                        {item.description}
                      </div>
                      {item.product && (
                        <div className="text-text-tertiary font-mono text-xs">{item.product.sku}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-text-primary font-mono">
                      {Number(item.quantity).toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary font-mono">
                      {formatCurrency(item.unitCost)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className="text-amber font-mono font-semibold"
                        style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                      >
                        {formatCurrency(item.lineTotal)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
              Totals
            </h3>
          </div>
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary font-mono">Subtotal:</span>
              <span className="text-text-primary font-mono">{formatCurrency(bill.subtotal)}</span>
            </div>
            {bill.taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Tax:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(bill.taxAmount)}
                </span>
              </div>
            )}
            {bill.shippingAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Shipping:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(bill.shippingAmount)}
                </span>
              </div>
            )}
            {bill.otherCharges > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Other Charges:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(bill.otherCharges)}
                </span>
              </div>
            )}
            <div className="border-t border-panel-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-text-secondary font-mono font-semibold">Total:</span>
                <span
                  className="text-xl text-amber font-mono font-bold"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
                >
                  {formatCurrency(bill.total)}
                </span>
              </div>
            </div>
            {bill.amountPaid > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Amount Paid:</span>
                <span className="text-success-green font-mono">
                  -{formatCurrency(bill.amountPaid)}
                </span>
              </div>
            )}
            <div className="border-t border-panel-border pt-2">
              <div className="flex justify-between">
                <span className="text-text-secondary font-mono font-semibold">Balance Due:</span>
                <span
                  className={`text-2xl font-mono font-bold ${bill.amountDue > 0 ? 'text-error-red' : 'text-success-green'}`}
                  style={{
                    textShadow:
                      bill.amountDue > 0
                        ? '0 0 10px rgba(220, 38, 38, 0.6)'
                        : '0 0 10px rgba(34, 197, 94, 0.6)',
                  }}
                >
                  {formatCurrency(bill.amountDue)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {bill.payments.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
            <div className="p-6 border-b border-panel-border">
              <div className="flex items-center space-x-2">
                <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                  Payment History
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Payment #
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Date
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Method
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Reference
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bill.payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-panel-border">
                      <td className="px-6 py-4">
                        <span className="text-text-primary font-mono font-semibold">
                          {payment.paymentNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {formatDate(payment.paymentDate)}
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {payment.paymentMethod.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 text-text-secondary font-mono">
                        {payment.checkNumber || payment.referenceNumber || '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-success-green font-mono font-semibold">
                          {formatCurrency(payment.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {(bill.notes || bill.internalNotes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {bill.notes && (
            <div className="relative stat-card">
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                    Notes
                  </h3>
                </div>
                <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
                  {bill.notes}
                </p>
              </div>
            </div>
          )}

          {bill.internalNotes && (
            <div className="relative stat-card">
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                    Internal Notes
                  </h3>
                </div>
                <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
                  {bill.internalNotes}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="relative w-full max-w-lg mx-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
                  <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
                    Record Payment
                  </h3>
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={recordPayment} className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Amount <span className="text-amber">*</span>
                  </label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    step="0.01"
                    min="0.01"
                    max={bill.amountDue}
                    required
                    className="form-input"
                    placeholder="0.00"
                  />
                  <div className="mt-1 text-text-tertiary font-mono text-xs">
                    Balance due: {formatCurrency(bill.amountDue)}
                  </div>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Payment Method <span className="text-amber">*</span>
                  </label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentMethod: e.target.value })
                    }
                    required
                    className="form-input"
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="ACH">ACH / Bank Transfer</option>
                    <option value="WIRE">Wire Transfer</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                    <option value="DEBIT_CARD">Debit Card</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Payment Date <span className="text-amber">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentDate: e.target.value })
                    }
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Check #
                  </label>
                  <input
                    type="text"
                    value={paymentData.checkNumber}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, checkNumber: e.target.value })
                    }
                    className="form-input"
                    placeholder="Check number"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Reference # (Transaction ID, Wire Ref, etc.)
                  </label>
                  <input
                    type="text"
                    value={paymentData.referenceNumber}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, referenceNumber: e.target.value })
                    }
                    className="form-input"
                    placeholder="Transaction ID or Wire reference"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Notes
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    className="form-input resize-none"
                    rows={2}
                    placeholder="Payment notes..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-6 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isRecordingPayment}
                    className="px-8 py-2 bg-success-green/20 hover:bg-success-green/30 text-success-green font-mono uppercase text-sm tracking-wider transition-all border border-success-green/30 disabled:opacity-50"
                  >
                    {isRecordingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Stat card component for summary stats
 */
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'amber' | 'green' | 'red' | 'steel';
}) {
  const colorClasses = {
    amber: 'text-amber',
    green: 'text-success-green',
    red: 'text-error-red',
    steel: 'text-steel',
  };

  const glowColors = {
    amber: 'rgba(255, 149, 0, 0.6)',
    green: 'rgba(34, 197, 94, 0.6)',
    red: 'rgba(220, 38, 38, 0.6)',
    steel: 'rgba(92, 140, 199, 0.6)',
  };

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 text-center">
        <div className="text-text-tertiary font-mono text-xs uppercase tracking-wider mb-1">
          {label}
        </div>
        <div
          className={`text-xl font-mono font-bold ${colorClasses[color]}`}
          style={{ textShadow: `0 0 8px ${glowColors[color]}` }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default BillDetailPage;
